import * as THREE from 'three';

const materialCache = new Map<string, THREE.MeshStandardMaterial>();

function canvasTexture(draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void, w = 256, h = 256) {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas2d');
  draw(ctx, w, h);
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function woodMaterial(repeat = 4, tint = 0x9c6b3c): THREE.MeshStandardMaterial {
  const key = `wood-${repeat}-${tint}`;
  const cached = materialCache.get(key);
  if (cached) return cached;
  const map = canvasTexture((ctx, w, h) => {
    ctx.fillStyle = '#6b4423';
    ctx.fillRect(0, 0, w, h);
    for (let y = 0; y < h; y += 6) {
      ctx.fillStyle = `rgba(0,0,0,${0.04 + (y % 12) * 0.002})`;
      ctx.fillRect(0, y, w, 2);
    }
    for (let x = 0; x < w; x += 28) {
      ctx.strokeStyle = 'rgba(255,255,255,0.06)';
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x + 8, h);
      ctx.stroke();
    }
  });
  map.repeat.set(repeat, repeat);
  const mat = new THREE.MeshStandardMaterial({
    map,
    color: tint,
    roughness: 0.62,
    metalness: 0.02,
  });
  materialCache.set(key, mat);
  return mat;
}

export function tileMaterial(repeat = 8): THREE.MeshStandardMaterial {
  const key = `tile-${repeat}`;
  const cached = materialCache.get(key);
  if (cached) return cached;
  const map = canvasTexture((ctx, w) => {
    const step = w / 8;
    for (let y = 0; y < 8; y++) {
      for (let x = 0; x < 8; x++) {
        ctx.fillStyle = (x + y) % 2 === 0 ? '#e8e0d4' : '#ddd4c8';
        ctx.fillRect(x * step, y * step, step - 2, step - 2);
      }
    }
  });
  map.repeat.set(repeat, repeat);
  const mat = new THREE.MeshStandardMaterial({ map, roughness: 0.85, metalness: 0 });
  materialCache.set(key, mat);
  return mat;
}

export function brickMaterial(): THREE.MeshStandardMaterial {
  const key = 'brick';
  const cached = materialCache.get(key);
  if (cached) return cached;
  const map = canvasTexture((ctx, w, h) => {
    ctx.fillStyle = '#8b3a2a';
    ctx.fillRect(0, 0, w, h);
    const bw = 48;
    const bh = 22;
    for (let row = 0; row < h / bh; row++) {
      const off = row % 2 ? bw / 2 : 0;
      for (let col = -1; col < w / bw + 1; col++) {
        ctx.fillStyle = row % 2 ? '#a04838' : '#7a3224';
        ctx.fillRect(col * bw + off + 2, row * bh + 2, bw - 4, bh - 4);
      }
    }
  }, 256, 128);
  map.repeat.set(3, 2);
  const mat = new THREE.MeshStandardMaterial({ map, roughness: 0.92, metalness: 0 });
  materialCache.set(key, mat);
  return mat;
}

export function grassMaterial(): THREE.MeshStandardMaterial {
  const key = 'grass';
  const cached = materialCache.get(key);
  if (cached) return cached;
  const map = canvasTexture((ctx, w, h) => {
    ctx.fillStyle = '#5a7a42';
    ctx.fillRect(0, 0, w, h);
    for (let i = 0; i < 4000; i++) {
      ctx.fillStyle = i % 3 === 0 ? '#6d8f52' : '#4d6b38';
      ctx.fillRect(Math.random() * w, Math.random() * h, 1, 2);
    }
  });
  map.repeat.set(6, 6);
  const mat = new THREE.MeshStandardMaterial({ map, roughness: 1, metalness: 0 });
  materialCache.set(key, mat);
  return mat;
}

export function emissiveScreen(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0x111118,
    emissive: color,
    emissiveIntensity: 0.65,
    roughness: 0.35,
    metalness: 0.1,
  });
}
